import { recordSearchEvent, markZeroResult as markZeroResultApi } from './api';

/* Per-tab session id so events can be grouped together when the same
 * user fires multiple searches in a session. Reset on each page load
 * is fine — we just want a coarse grouping signal, not an identity. */
const SESSION_KEY = 'wp.searchSessionId';
/* Exported so listing-view events (P25009-385) group under the same session id
 * as search events, instead of inventing a second, unrelated one. */
export const getSessionId = () => {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return '';
  }
};

/* Per-tab dedup. The aim is to collapse the Autocomplete-onChange
 * + form-onSubmit double-fire that comes from a single user intent,
 * not to deduplicate across users — so module-scoped (per-tab) state
 * is the correct scope. Each tab maintains its own state. */
const lastByKind = {
  plate: { key: '', at: 0 },
  dim: { key: '', at: 0 },
  zero: { key: '', at: 0 },
};
const DEDUP_WINDOW_MS = 2000;

const shouldRecord = (kind, key) => {
  const now = Date.now();
  const slot = lastByKind[kind];
  if (key === slot.key && now - slot.at < DEDUP_WINDOW_MS) return false;
  slot.key = key;
  slot.at = now;
  return true;
};

const firstString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return '';
};

/* For display fields, treat all-numeric values as IDs (e.g. "9824"
 * is a trim ID, not a trim name) and skip them in favour of the next
 * candidate. */
const firstDisplayString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() && !/^\d+$/.test(c.trim())) {
      return c.trim();
    }
  }
  return '';
};

/* Does `make` belong to the car this search URL is actually about?
 *
 * pub_categoryLevel1 is written by two flows that mangle the value
 * differently for Sharetribe Console: the landing page replaces spaces with
 * '-' (SearchSection), RegNumInput replaces '-' with 'n'. So compare on the
 * alphanumeric skeleton and also try the 'n' form, otherwise a hyphenated
 * make would never match its own search URL.
 *
 * Exported for the unit test — not part of the tracking API. */
export const makeMatchesUrl = (make, urlMake) => {
  if (!make || !urlMake) return false;
  const strip = s => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const target = strip(urlMake);
  if (!target) return false;
  return strip(make) === target || strip(String(make).replace(/-/g, 'n')) === target;
};

const firstNumber = (...candidates) => {
  for (const c of candidates) {
    if (c === null || c === undefined || c === '') continue;
    const n = typeof c === 'number' ? c : parseFloat(c);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/* Pull { make, model, variant, hp, year } plus original-case display
 * fields out of the licence-plate lookup response. Mirrors the data
 * shapes that CarInfoSidebar and RegNumInput already consume — see
 * CarInfoSidebar.pickCar for the envelope variations. Returns null
 * when no make can be determined, which signals the caller to skip
 * tracking. */
export const extractResolvedCar = data => {
  if (!data) return null;
  const car =
    data.car ||
    (Array.isArray(data.data) ? data.data[0] : null) ||
    data.result ||
    data ||
    {};

  const displayMake = firstString(
    car?.make?.name,
    car?.brand,
    data?.result?.brand,
    data?.result?.make,
    data?.make
  );

  if (!displayMake) return null;

  const displayModel = firstString(
    car?.model?.name,
    car?.series?.name,
    typeof car?.series === 'string' ? car.series : '',
    typeof car?.model === 'string' ? car.model : '',
    car?.sales_name,
    data?.result?.model,
    data?.result?.series
  );

  /* Strip the make/model prefix off a full "car_name" string so a
   * value like "Volvo XC60 D5 AWD" becomes just "D5 AWD" — otherwise
   * using car_name as the variant double-prints the make/model that
   * already lives in the head label. */
  const stripPrefix = (s, prefix) => {
    if (!s || !prefix) return s || '';
    const lower = s.toLowerCase();
    const lowerPrefix = prefix.toLowerCase();
    if (lower.startsWith(lowerPrefix + ' ')) return s.slice(prefix.length + 1).trim();
    if (lower === lowerPrefix) return '';
    return s;
  };
  const carName = firstString(
    data?.result?.car_name,
    data?.car_name,
    car?.car_name
  );
  const carNameTrimmed = stripPrefix(stripPrefix(carName, displayMake), displayModel);

  /* Variant slot: distinct from "model" (e.g. "XC60") — this is the
   * trim/engine designation like "Polestar Engineered" or
   * "D4 Geartronic". firstDisplayString skips numeric IDs that some
   * upstream sources put in these fields (e.g. trim_id "9824"). The
   * stripped car_name comes in as a richer fallback so cars without
   * a discrete trim record still get a meaningful sub-label. */
  const displayVariant = firstDisplayString(
    car?.trim?.name,
    typeof car?.trim === 'string' ? car.trim : '',
    car?.name,
    car?.sales_name !== displayModel ? car?.sales_name : '',
    data?.result?.series !== displayModel ? data?.result?.series : '',
    carNameTrimmed
  );

  /* Year validation: upstream "year" fields are unreliable (we've
   * seen "44" come back as a year). Clamp to a plausible automotive
   * range, otherwise the dashboard ends up with rows like
   * "Peugeot Partner · 44" that look like data corruption to a
   * client reading the report. */
  const CURRENT_YEAR = new Date().getFullYear();
  const yearRaw = firstNumber(
    car?.year,
    car?.model_year,
    car?.year_from,
    car?.end_year,
    data?.result?.model_year,
    data?.result?.year,
    data?.year
  );
  const year = (yearRaw !== null && yearRaw >= 1900 && yearRaw <= CURRENT_YEAR + 1)
    ? yearRaw
    : null;

  const hp = firstNumber(
    car?.engine?.horsepower,
    car?.technical?.horsepower,
    car?.horsepower,
    car?.power_hp,
    data?.result?.power_hp,
    data?.result?.horsepower
  );

  return {
    make: displayMake.toLowerCase(),
    model: displayModel.toLowerCase(),
    variant: displayVariant.toLowerCase(),
    displayMake,
    displayModel,
    displayVariant,
    /* Full "Volvo XC60 D4 Geartronic, 190hp, 2020" string when the
     * upstream API provided it — the dashboard prefers this over the
     * piecemeal make+model concatenation. */
    displayLabel: carName,
    hp,
    year,
  };
};

/* Fired on every successful licence-plate lookup. The dashboard needs
 * the raw plate string (rawInput) for the "X users searched PKE23J"
 * total, and the resolved car for aggregation across make/model.
 * seriesName is the UI-only motor label the user picked from the
 * "Välj motor" dropdown — attached to resolvedCar so it flows
 * through to the dashboard. */
export const recordPlateSearch = ({ rawInput, data, resultCount, seriesName } = {}) => {
  const resolvedCar = extractResolvedCar(data);
  if (!resolvedCar) return;
  if (seriesName) resolvedCar.seriesName = seriesName;
  const dedupKey = `${rawInput || ''}|${resolvedCar.make}|${resolvedCar.model}`.toLowerCase();
  if (!shouldRecord('plate', dedupKey)) return;
  recordSearchEvent({
    sessionId: getSessionId(),
    searchType: 'plate',
    rawInput: rawInput || '',
    resolvedCar,
    resultCount: typeof resultCount === 'number' ? resultCount : null,
  }).catch(() => {
    /* Tracking failures must never block the user flow. */
  });
};

/* Fired on every CarInfoSidebar dim-list click. Each click is one
 * filter-application event, attributed to the currently-resolved car
 * so the dashboard can show "for Volvo XC60, users most often click
 * 225/45R17 wheels". */
export const recordDimClick = ({ data, filter } = {}) => {
  const resolvedCar = extractResolvedCar(data);
  if (!resolvedCar || !filter) return;
  const filterKey = [
    filter.adType,
    filter.tireWidth,
    filter.profile,
    filter.diameter,
  ]
    .filter(Boolean)
    .join('|');
  if (!filterKey) return;
  const dedupKey = `${resolvedCar.make}|${resolvedCar.model}|${filterKey}`.toLowerCase();
  if (!shouldRecord('dim', dedupKey)) return;
  recordSearchEvent({
    sessionId: getSessionId(),
    searchType: 'dim_click',
    rawInput: filter.label || '',
    resolvedCar,
    filter,
  }).catch(() => {
    /* Tracking failures must never block the user flow. */
  });
};

/* Fired when a plate-driven SearchPage load resolves to 0 listings —
 * the strongest "unmet demand" signal we collect. The filter arg
 * captures which combo produced the empty result so the dashboard
 * can show *which* spec failed, not just the rollup count. Accepts
 * either a `data` envelope (we extract internally) or a pre-built
 * `resolvedCar` for cases where the caller already knows make/model
 * (e.g. SearchPage hook on a cold paste-URL load with no Redux plate
 * state).
 *
 * `expectedMake` is pub_categoryLevel1 from the search URL and is
 * REQUIRED. state.car in Redux is sticky — it is set once by the
 * landing-page plate search and cleared only by "Ny sökning" or a
 * page reload, so without this check every later empty search in the
 * tab (a keyword search, a brand filter, anything) was filed as
 * unmet demand for that one car. Requiring the URL to still be about
 * the same make makes the attribution correct by construction.
 *
 * The final count-once-per-(session, car, filter) rule lives on the
 * server, in api/searchKeywords/markZeroResult — it has to, because a
 * browser running a cached bundle would otherwise keep double-counting
 * for days after a deploy. The 2-second window below stays as a cheap
 * way to collapse a re-render storm before it reaches the network. */
export const markZeroResult = ({
  data,
  resolvedCar: providedCar,
  rawInput,
  filter,
  expectedMake,
} = {}) => {
  const resolvedCar = providedCar || extractResolvedCar(data);
  if (!resolvedCar || !resolvedCar.make) return;
  if (!makeMatchesUrl(resolvedCar.make, expectedMake)) return;
  const filterKey = filter
    ? [filter.adType, filter.tireWidth, filter.profile, filter.diameter]
        .filter(Boolean)
        .join('|')
    : '';
  const dedupKey = `${resolvedCar.make}|${resolvedCar.model}|${resolvedCar.variant}|${resolvedCar.year || ''}|${filterKey}`.toLowerCase();
  if (!shouldRecord('zero', dedupKey)) return;
  markZeroResultApi({
    sessionId: getSessionId(),
    rawInput: rawInput || '',
    resolvedCar,
    filter: filter || undefined,
  }).catch(() => {
    /* Tracking failures must never block the user flow. */
  });
};
